/* eslint-disable */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import Cookies from 'js-cookie';
import { ErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { BackButton, PrimaryButton, SecondaryButton, OutlinedDeleteButton } from '../../../../component/buttonComponent/ButtonComponent';
import Title from '../../../../component/title/Title';
import TextArea from '../../../../component/textAreaLimit/TextArea';
import Modal from '../../../../component/modal/Modal';
import { APICreateFuelPolicy } from '../../../../config/restAPI/PostAPI';
import { APIUpdateFuelPolicy } from '../../../../config/restAPI/PatchAPI';
import { APIDeleteFuelPolicies } from '../../../../config/restAPI/DeleteAPI';
import '../../batteryConfigurator/addBatteryConfig/addBatteryConfig.css';
import './addFuelConfig.css';
import FuelMapTable from '../../../../component/fuelMap/FuelMapTable';
import FuelEquation from '../../../../component/fuelEquation/FuelEquation';
import BatteryPercentageStatusTable from '../../../../component/mapBattery/percentageBattery';
import '../../../../component/mapBattery/percentageBattery.css';
import {
    LOCKED_FIELD_KEYS_WHEN_ACTIVE,
    validateFuelPolicyForSave,
} from '../fuelPolicyValidation';
import {
    apiPolicyToForm,
    buildFuelPolicyPayload,
    extractFuelPolicyApiErrorMessage,
    FUEL_LEDGER_PROFILE_DEFAULTS,
    isFuelPolicySaveSuccess,
    isVoltageTelemetryInput,
    parseFuelPolicyFromSaveResponse,
    resolveFuelPolicyLastEditedAt,
} from '../fuelPolicyApiMapping';
import {
    FUEL_MAP_EQUATION_INVERSE_FORMULAS_FOR_LITRE,
    fuelMapEquationFormulaForFormTelemetry,
} from '../fuelMapEquation';
import {
    fetchFuelPolicyAttachmentCounts,
    getCachedFuelPolicyAssetsAttachedCount,
    getFuelPolicyAssetsAttachedCount,
} from '../fuelPolicyAssetsAttached';
import { fuelPolicyFieldTipsForForm } from '../fuelPolicyFieldTooltips';
import { FpFieldInfoTrigger, FpFieldTooltipProvider } from './FpFieldTooltip';
import DropDownList from '../../../../component/iconDropDown/iconDropDown';

const formFallback = ({ error, resetErrorBoundary }) => (
    <div style={{ padding: '2rem', color: '#101828' }}>
        <p>Something went wrong loading this form.</p>
        <button type="button" onClick={resetErrorBoundary}>Try again</button>
        <pre style={{ fontSize: '12px', marginTop: '1rem' }}>{String(error)}</pre>
    </div>
);

const TELEMETRY_INPUT_OPTIONS = [
    { value: 'litre', label: 'Litre' },
    { value: 'percentage', label: 'Percentage' },
    { value: 'litrePercentage', label: 'Litre & Percentage' },
    { value: 'voltage', label: 'Voltage' },
];

const LEVEL_SOURCE_OPTIONS = [
    { value: 'table', label: 'Map Table' },
    { value: 'equation', label: 'Map Equation' },
    { value: 'tankCapacity', label: 'Fuel Tank Capacity' },
];

const POLICY_STATUS_OPTIONS = [
    { value: 'DRAFT', label: 'Draft' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'ARCHIVED', label: 'Archived' },
];

function FpDropDown({
    options,
    selectedValue,
    onChange,
    placeholder,
    disabled = false,
    error = false,
}) {
    const selected = options.find((o) => o.value === selectedValue) || null;
    return (
        <div className={`fp-dropdown-wrap${error ? ' fp-dropdown-wrap--error' : ''}`}>
            <DropDownList
                placeholder={placeholder}
                selectedValue={selected}
                setSelectedValue={(option) => onChange(option?.value ?? '')}
                options={options}
                width="100%"
                controlHeight={44}
                menuPortal
                isDisabled={disabled}
            />
        </div>
    );
}

/** Default fuel policy percentage bands (Percentage From → To, State). */
const DEFAULT_PERCENTAGE_MAPPING = [
    { percentageFrom: 100, percentageTo: 75, state: 'Excellent' },
    { percentageFrom: 74, percentageTo: 50, state: 'Good' },
    { percentageFrom: 49, percentageTo: 25, state: 'Low' },
    { percentageFrom: 24, percentageTo: 0, state: 'Refuel Required' },
];

function FpFieldRow({
    label, hint, locked, unit, stack, fill, children, fieldTip,
}) {
    const showHintUnder = Boolean(hint) && !fieldTip;
    const rowClass = ['fp-row', stack && 'fp-row--stack', fill && 'fp-row--fill'].filter(Boolean).join(' ');
    return (
        <div className={rowClass}>
            <div className={`fp-label${fieldTip ? ' fp-label--has-tip' : ''}`}>
                <span className="fp-label-text">{label}</span>
                {locked ? <LockIcon sx={{ fontSize: 16, verticalAlign: 'middle', ml: 0.5 }} /> : null}
                {fieldTip ? <FpFieldInfoTrigger fieldTip={fieldTip} /> : null}
                {showHintUnder ? <span className="fp-label-hint">{hint}</span> : null}
            </div>
            <div className={`fp-input-cell${stack ? ' fp-input-cell--stack' : ''}`}>
                {children}
                {unit && !stack ? <span className="fp-unit-suffix">{unit}</span> : null}
            </div>
        </div>
    );
}

/** Tier 2 tracking + Tier 0 GNSS — one bordered box, unit inside on the right (no split border). */
function FpNumberInput({
    unit, value, onChange, disable, error, errorMessage, tier0 = false, narrow = false, placeholder,
    step = 'any',
}) {
    const boxClass = [
        'fp-number-input',
        tier0 && 'fp-number-input--t0',
        narrow && 'fp-number-input--narrow',
        error && 'fp-number-input--error',
    ].filter(Boolean).join(' ');

    return (
        <div className="fp-validated-control">
            <div className={boxClass}>
                <input
                    type="number"
                    step={step}
                    className="fp-number-input__field"
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    disabled={disable}
                />
                {unit ? <span className="fp-number-input__unit">{unit}</span> : null}
            </div>
            {errorMessage ? <div className="fp-field-error fp-field-error--below">{errorMessage}</div> : null}
        </div>
    );
}


function scrollToFirstValidationError() {
    requestAnimationFrame(() => {
        const el = document.querySelector(
            '.fp-field-error--below, .fp-field-error, .fp-number-input--error, .fuel-config-select--error, .fp-dropdown-wrap--error',
        );
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
}

function validationSummaryMessage(errors, status) {
    const messages = Object.values(errors).filter(Boolean);
    if (messages.length === 0) {
        if (status === 'ACTIVE') {
            return 'Fix validation errors before activating this policy.';
        }
        return 'Policy name is required to save as draft.';
    }
    if (messages.length === 1) return messages[0];
    return `${messages.length} validation issues — see highlighted fields below.`;
}


const emptyForm = () => ({
    name: '',
    status: 'DRAFT',
    tankCapacityL: '',
    refillDetectionThresholdL: '',
    theftDrainThresholdL: '',
    spikeFilterVolumeL: '',
    sloshSettleDurationSec: '',
    sloshToleranceL: '',
    microRefillL: '',
    gapCapMin: String(FUEL_LEDGER_PROFILE_DEFAULTS.gapCapMin),
    drainSpanMin: String(FUEL_LEDGER_PROFILE_DEFAULTS.drainSpanMin),
    maxBurnRateLh: String(FUEL_LEDGER_PROFILE_DEFAULTS.maxBurnRateLh),
    telemetryInput: '',
    voltageMin: '',
    voltageMax: '',
    tankCapacityDetectionEnabled: false,
    tableMapEnabled: false,
    equationMapEnabled: false,
    fuelMapRows: [],
    equationMap: { a2: 0, a1: 0, a0: 0 },
    idleLhr: '',
    cityL100km: '',
    avgL100km: '',
    hwyL100km: '',
    penaltyPct: '',
    penaltyIntervalKmh: '',
    percentageMapping: DEFAULT_PERCENTAGE_MAPPING,
    description: '',
});

const policyToForm = (policy) => {
    if (!policy) return emptyForm();
    const mapped = apiPolicyToForm(policy);
    if (!mapped) return emptyForm();
    const percentageMapping = mapped.percentageMapping?.length
        ? mapped.percentageMapping
        : DEFAULT_PERCENTAGE_MAPPING;
    return { ...mapped, percentageMapping };
};

const serializeComparable = (f) => JSON.stringify({
    name: f.name,
    status: f.status,
    tankCapacityL: f.tankCapacityL,
    refillDetectionThresholdL: f.refillDetectionThresholdL,
    theftDrainThresholdL: f.theftDrainThresholdL,
    spikeFilterVolumeL: f.spikeFilterVolumeL,
    sloshSettleDurationSec: f.sloshSettleDurationSec,
    sloshToleranceL: f.sloshToleranceL,
    microRefillL: f.microRefillL,
    gapCapMin: f.gapCapMin,
    drainSpanMin: f.drainSpanMin,
    maxBurnRateLh: f.maxBurnRateLh,
    telemetryInput: f.telemetryInput,
    voltageMin: f.voltageMin,
    voltageMax: f.voltageMax,
    tankCapacityDetectionEnabled: f.tankCapacityDetectionEnabled,
    tableMapEnabled: f.tableMapEnabled,
    equationMapEnabled: f.equationMapEnabled,
    fuelMapRows: f.fuelMapRows,
    equationMap: f.equationMap,
    idleLhr: f.idleLhr,
    cityL100km: f.cityL100km,
    avgL100km: f.avgL100km,
    hwyL100km: f.hwyL100km,
    penaltyPct: f.penaltyPct,
    penaltyIntervalKmh: f.penaltyIntervalKmh,
    percentageMapping: f.percentageMapping,
    description: f.description,
});

function AddFuelConfigInner() {
    const navigate = useNavigate();
    const token = Cookies.get('jwtToken');
    const { state } = useLocation();
    const agencyId = Cookies.get('agencyUUID');
    const role = Cookies.get('role');

    const [loading, setLoading] = useState(false);
    const [snErr, setSNErr] = useState(false);
    const [SNErrMessage, setSNErrMessage] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [form, setForm] = useState(emptyForm());
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentPolicyId, setCurrentPolicyId] = useState(null);
    const [assetsAttached, setAssetsAttached] = useState(0);
    const [assetsWithOverridesCount, setAssetsWithOverridesCount] = useState(0);
    const [deleteModal, setDeleteModal] = useState(false);
    const [activeConfirmOpen, setActiveConfirmOpen] = useState(false);
    const baselineRef = useRef('');
    const initialStatusRef = useRef('DRAFT');

    /** ACTIVE policies lock Tier 0/1 fields for clients; Tier 2 tolerances stay editable. Admins can always edit.
     *  Lock is based on the status at load time, not the in-form dropdown, so demotion requires a save first. */
    const isLocked = (key) => {
        if (role === 'System Administrator') return false;
        return initialStatusRef.current === 'ACTIVE' && LOCKED_FIELD_KEYS_WHEN_ACTIVE.has(key);
    };

    useEffect(() => {
        if (state && state.id) {
            setIsEditMode(true);
            setCurrentPolicyId(state.id);
            setAssetsAttached(state.assetsAttached ?? 0);
            setAssetsWithOverridesCount(state.assetsWithOverridesCount ?? 0);
            initialStatusRef.current = state.status || 'DRAFT';
            const next = policyToForm(state);
            setForm(next);
            baselineRef.current = serializeComparable(next);
        } else {
            const next = emptyForm();
            setForm(next);
            baselineRef.current = serializeComparable(next);
            initialStatusRef.current = 'DRAFT';
        }
    }, [state]);

    useEffect(() => {
        if (!token || !agencyId || !currentPolicyId) {
            return undefined;
        }

        // List navigation already passes a pre-calculated count — avoid rescanning all devices.
        if (state?.assetsAttached !== undefined) {
            return undefined;
        }

        const cachedCount = getCachedFuelPolicyAssetsAttachedCount(agencyId, currentPolicyId);
        if (cachedCount != null) {
            setAssetsAttached(cachedCount);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            const attachmentCounts = await fetchFuelPolicyAttachmentCounts(token, agencyId);
            if (cancelled || attachmentCounts == null) {
                return;
            }
            setAssetsAttached(getFuelPolicyAssetsAttachedCount(attachmentCounts, currentPolicyId));
        })();
        return () => {
            cancelled = true;
        };
    }, [token, agencyId, currentPolicyId, state?.assetsAttached]);

    const isDirty = useMemo(() => serializeComparable(form) !== baselineRef.current, [form]);

    const setField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleTableMapToggle = (e) => {
        if (isLocked('tableMapEnabled')) return;
        const checked = e.target.checked;
        setForm((prev) => ({
            ...prev,
            tableMapEnabled: checked,
            equationMapEnabled: checked ? false : prev.equationMapEnabled,
            tankCapacityDetectionEnabled: checked ? false : prev.tankCapacityDetectionEnabled,
        }));
    };

    const handleEquationMapToggle = (e) => {
        if (isLocked('equationMapEnabled')) return;
        const checked = e.target.checked;
        setForm((prev) => ({
            ...prev,
            equationMapEnabled: checked,
            tableMapEnabled: checked ? false : prev.tableMapEnabled,
            tankCapacityDetectionEnabled: checked ? false : prev.tankCapacityDetectionEnabled,
        }));
    };

    const handleEquationChange = (field, value) => {
        if (isLocked('equationMap')) return;
        setForm((prev) => ({ ...prev, equationMap: { ...prev.equationMap, [field]: value } }));
    };

    const levelMappingOptions = useMemo(() => {
        if (form.telemetryInput === 'litrePercentage') {
            return LEVEL_SOURCE_OPTIONS.filter((o) => o.value === 'tankCapacity');
        }
        return LEVEL_SOURCE_OPTIONS;
    }, [form.telemetryInput]);

    const isVoltageMode = isVoltageTelemetryInput(form.telemetryInput);

    const handleTelemetryInputSelect = (value) => {
        if (isLocked('telemetryInput')) return;
        setForm((prev) => {
            const next = { ...prev, telemetryInput: value };
            if (isVoltageTelemetryInput(value)) {
                return {
                    ...next,
                    tankCapacityDetectionEnabled: false,
                    tableMapEnabled: false,
                    equationMapEnabled: true,
                };
            }
            if (isVoltageTelemetryInput(prev.telemetryInput)) {
                return {
                    ...next,
                    tableMapEnabled: false,
                    equationMapEnabled: false,
                    tankCapacityDetectionEnabled: false,
                };
            }
            if (value === 'litrePercentage') {
                return {
                    ...next,
                    tankCapacityDetectionEnabled: true,
                    tableMapEnabled: false,
                    equationMapEnabled: false,
                };
            }
            return next;
        });
    };

    const tankCap = parseFloat(form.tankCapacityL);
    const quarterHint = Number.isFinite(tankCap) && tankCap > 0 ? (tankCap / 4).toFixed(2) : 'tank capacity / 4';

    const performSave = async () => {
        const params = buildFuelPolicyPayload(form, currentPolicyId);
        try {
            let result;
            if (isEditMode) {
                result = await APIUpdateFuelPolicy(token, setLoading, agencyId, params);
            } else {
                result = await APICreateFuelPolicy(token, setLoading, agencyId, params);
            }
            if (isFuelPolicySaveSuccess(result)) {
                setLoading(false);
                baselineRef.current = serializeComparable(form);
                const savedPolicy = parseFuelPolicyFromSaveResponse(result);
                const savedPolicyId = currentPolicyId || savedPolicy?.id;
                const lastEditedAt = resolveFuelPolicyLastEditedAt(savedPolicy)
                    || new Date().toISOString();
                navigate('/inventory/fuelconfigurator', {
                    state: {
                        snackbarStatus: 'success',
                        snackbarTitle: 'Success',
                        snackbarSub: isEditMode ? 'Fuel policy updated.' : 'Fuel policy created.',
                        fuelPolicyLastEdited: savedPolicyId
                            ? { id: savedPolicyId, lastEditedAt }
                            : null,
                    },
                });
                return;
            }
            setLoading(false);
            setSNErr(true);
            setSNErrMessage(extractFuelPolicyApiErrorMessage(result));
        } catch (err) {
            setSNErr(true);
            setSNErrMessage(extractFuelPolicyApiErrorMessage(err) || 'An unexpected error occurred. Please try again.');
            setLoading(false);
        }
    };

    const handleStatusChange = (nextStatus) => {
        if (!nextStatus) {
            return;
        }
        setField('status', nextStatus);
        setFieldErrors((prev) => {
            if (!prev.status) {
                return prev;
            }
            const { status, ...rest } = prev;
            return rest;
        });
        if (nextStatus !== 'ACTIVE') {
            setSNErr(false);
            setSNErrMessage('');
        }
    };

    const handleSave = () => {
        setSNErr(false);
        setFieldErrors({});
        const v = validateFuelPolicyForSave(form, { assetsAttached });
        if (!v.ok) {
            setFieldErrors(v.errors);
            const isStatusBlockOnly = v.errors.status && Object.keys(v.errors).length === 1;
            if (isStatusBlockOnly) {
                requestAnimationFrame(() => {
                    document.querySelector('.fp-archive-warning')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                });
            } else {
                setSNErr(true);
                setSNErrMessage(validationSummaryMessage(v.errors, form.status));
                scrollToFirstValidationError();
            }
            return;
        }
        if (isEditMode && initialStatusRef.current === 'ACTIVE' && isDirty) {
            setActiveConfirmOpen(true);
            return;
        }
        performSave();
    };

    const handleActiveConfirmProceed = () => {
        setActiveConfirmOpen(false);
        performSave();
    };

    const handleBack = () => {
        navigate('/inventory/fuelconfigurator', { replace: true });
    };

    const levelSourceValue = form.tankCapacityDetectionEnabled
        ? 'tankCapacity'
        : form.tableMapEnabled
            ? 'table'
            : form.equationMapEnabled
                ? 'equation'
                : '';

    const handleLevelSourceSelect = (v) => {
        if (v === 'table') {
            handleTableMapToggle({ target: { checked: true } });
        } else if (v === 'equation') {
            handleEquationMapToggle({ target: { checked: true } });
        } else if (v === 'tankCapacity') {
            if (isLocked('tankCapacityDetectionEnabled')) return;
            setForm((prev) => ({
                ...prev,
                tankCapacityDetectionEnabled: true,
                tableMapEnabled: false,
                equationMapEnabled: false,
            }));
        } else if (v === '') {
            if (isLocked('tableMapEnabled') || isLocked('equationMapEnabled') || isLocked('tankCapacityDetectionEnabled')) return;
            setForm((prev) => ({
                ...prev,
                tableMapEnabled: false,
                equationMapEnabled: false,
                tankCapacityDetectionEnabled: false,
            }));
        }
    };

    const handleDelete = async () => {
        const params = { oid: [currentPolicyId] };
        try {
            const result = await APIDeleteFuelPolicies(token, setLoading, agencyId, params);
            const status = result?.data?.status ?? result?.status ?? result?.response?.data?.status;
            if (status === 200) {
                setLoading(false);
                navigate('/inventory/fuelconfigurator', {
                    state: {
                        snackbarStatus: 'success',
                        snackbarTitle: 'Success',
                        snackbarSub: 'Fuel policy deleted.',
                    },
                });
            } else {
                setLoading(false);
                setDeleteModal(false);
                const apiMsg = result?.data?.error?.message
                    || result?.response?.data?.error?.message
                    || result?.message
                    || 'Failed to delete fuel policy.';
                setSNErr(true);
                setSNErrMessage(apiMsg);
            }
        } catch (err) {
            setLoading(false);
            setDeleteModal(false);
            setSNErr(true);
            setSNErrMessage('Failed to delete fuel policy.');
        }
    };

    const spikeNum = parseFloat(form.spikeFilterVolumeL);

    const rawVer = state?.version ?? state?.policyVersion;
    const versionLabel = rawVer != null && rawVer !== ''
        ? (String(rawVer).toLowerCase().startsWith('v') ? String(rawVer) : `v${rawVer}`)
        : null;

    const statusBadgeClass = form.status === 'ACTIVE'
        ? 'fp-status-badge--active'
        : form.status === 'ARCHIVED'
            ? 'fp-status-badge--archived'
            : 'fp-status-badge--draft';

    const statusBadgeLabel = versionLabel ? `${form.status} · ${versionLabel}` : form.status;
    const archiveWarningMessage = fieldErrors.status || '';

    const fieldTips = useMemo(
        () => fuelPolicyFieldTipsForForm(quarterHint, spikeNum),
        [quarterHint, spikeNum],
    );
    const policyNamePlaceholder = 'e.g. 400L Diesel — Hino 500 Series';

    return (
        <div className="add-device-container fp-page">
            <div className="add-device-header-section">
                <div className="add-device-header-container">
                    <BackButton label="Back to inventory" btnClickBack={handleBack} color="#FFFFFF" />
                </div>
            </div>
            <div className="add-device-form-section">
                <div className="add-device-form-container fp-shell">
                    <div className="add-device-form-header">
                        <div className="add-device-form-header-content">
                            <Title
                                title={isEditMode ? 'Edit Fuel Configuration' : 'Add Fuel Configuration'}
                                deleteSubtitle
                                titleSize="18px"
                                lineHeight="28px"
                            />
                        </div>
                    </div>

                    <div className="fp-main-card">
                        <div className="fp-card-hdr">
                            <div className="fp-card-hdr-info">
                                <div className="fp-card-hdr-title-row">
                                    <span className="fp-card-hdr-title" title={form.name || 'Policy name'}>
                                        {form.name?.trim() || 'Policy name'}
                                    </span>
                                    <span className={`fp-status-badge ${statusBadgeClass}`}>{statusBadgeLabel}</span>
                                </div>
                                <div className="fp-card-hdr-meta">
                                    {assetsAttached}
                                    {' '}
                                    assets
                                </div>
                            </div>
                            {archiveWarningMessage ? (
                                <div className="fp-archive-warning" role="alert">
                                    {archiveWarningMessage}
                                </div>
                            ) : null}
                        </div>


                        {snErr && SNErrMessage ? (
                            <div role="alert" className="fp-alert-inline">{SNErrMessage}</div>
                        ) : null}

                        <div className="fp-policy-band">
                            <div className="fp-sec-title fp-sec-title--t1">Policy</div>
                            <div className="fp-policy-grid">
                                <FpFieldRow fill label="Policy name" fieldTip={fieldTips.name} locked={isLocked('name')}>
                                    <div className="add-device-input fp-control">
                                        <input
                                            type="text"
                                            className={`fuel-config-select${fieldErrors.name ? ' fuel-config-select--error' : ''}`}
                                            placeholder={policyNamePlaceholder}
                                            value={form.name}
                                            disabled={isLocked('name')}
                                            onChange={(e) => setField('name', e.target.value)}
                                        />
                                        {fieldErrors.name ? (
                                            <div className="fp-field-error">{fieldErrors.name}</div>
                                        ) : null}
                                    </div>
                                </FpFieldRow>

                                <FpFieldRow fill label="Policy status">
                                    <FpDropDown
                                        options={POLICY_STATUS_OPTIONS}
                                        selectedValue={form.status}
                                        onChange={handleStatusChange}
                                        placeholder="Select policy status"
                                        error={!!archiveWarningMessage}
                                    />
                                </FpFieldRow>
                            </div>
                        </div>

                        <div className="fp-two-col">
                            <div className="fp-col">
                                <div className="fp-sec-title fp-sec-title--t2">
                                    <LockOpenIcon sx={{ fontSize: 16 }} />
                                    Tracking Tolerances
                                </div>

                                <FpFieldRow label="Tank capacity" fieldTip={fieldTips.tankCapacityL} locked={isLocked('tankCapacityL')}>
                                    <FpNumberInput
                                        unit="L"
                                        error={!!fieldErrors.tankCapacityL}
                                        errorMessage={fieldErrors.tankCapacityL}
                                        value={form.tankCapacityL}
                                        disable={isLocked('tankCapacityL')}
                                        onChange={(e) => setField('tankCapacityL', e.target.value)}
                                    />
                                </FpFieldRow>

                                <FpFieldRow label="Refill threshold" fieldTip={fieldTips.refillDetectionThresholdL}>
                                    <FpNumberInput
                                        unit="L"
                                        error={!!fieldErrors.refillDetectionThresholdL}
                                        errorMessage={fieldErrors.refillDetectionThresholdL}
                                        value={form.refillDetectionThresholdL}
                                        onChange={(e) => setField('refillDetectionThresholdL', e.target.value)}
                                    />
                                </FpFieldRow>

                                <FpFieldRow label="Drain (theft)" fieldTip={fieldTips.theftDrainThresholdL}>
                                    <FpNumberInput
                                        unit="L"
                                        error={!!fieldErrors.theftDrainThresholdL}
                                        errorMessage={fieldErrors.theftDrainThresholdL}
                                        value={form.theftDrainThresholdL}
                                        onChange={(e) => setField('theftDrainThresholdL', e.target.value)}
                                    />
                                </FpFieldRow>

                                <FpFieldRow label="Spike filter" fieldTip={fieldTips.spikeFilterVolumeL}>
                                    <FpNumberInput
                                        unit="L"
                                        error={!!fieldErrors.spikeFilterVolumeL}
                                        errorMessage={fieldErrors.spikeFilterVolumeL}
                                        value={form.spikeFilterVolumeL}
                                        onChange={(e) => setField('spikeFilterVolumeL', e.target.value)}
                                    />
                                </FpFieldRow>

                                <FpFieldRow label="Slosh duration" fieldTip={fieldTips.sloshSettleDurationSec}>
                                    <FpNumberInput
                                        unit="sec"
                                        error={!!fieldErrors.sloshSettleDurationSec}
                                        errorMessage={fieldErrors.sloshSettleDurationSec}
                                        value={form.sloshSettleDurationSec}
                                        onChange={(e) => setField('sloshSettleDurationSec', e.target.value)}
                                    />
                                </FpFieldRow>

                                <FpFieldRow label="Slosh tolerance" fieldTip={fieldTips.sloshToleranceL}>
                                    <FpNumberInput
                                        unit="L"
                                        error={!!fieldErrors.sloshToleranceL}
                                        errorMessage={fieldErrors.sloshToleranceL}
                                        value={form.sloshToleranceL}
                                        onChange={(e) => setField('sloshToleranceL', e.target.value)}
                                    />
                                </FpFieldRow>

                                <FpFieldRow label="Micro-refill" fieldTip={fieldTips.microRefillL}>
                                    <FpNumberInput
                                        unit="L"
                                        step="0.5"
                                        placeholder="Off"
                                        error={!!fieldErrors.microRefillL}
                                        errorMessage={fieldErrors.microRefillL}
                                        value={form.microRefillL}
                                        onChange={(e) => setField('microRefillL', e.target.value)}
                                    />
                                </FpFieldRow>

                                <FpFieldRow label="Offline gap cap" fieldTip={fieldTips.gapCapMin}>
                                    <FpNumberInput
                                        unit="min"
                                        step="5"
                                        error={!!fieldErrors.gapCapMin}
                                        errorMessage={fieldErrors.gapCapMin}
                                        value={form.gapCapMin}
                                        onChange={(e) => setField('gapCapMin', e.target.value)}
                                    />
                                </FpFieldRow>

                                <FpFieldRow label="Drain window" fieldTip={fieldTips.drainSpanMin}>
                                    <FpNumberInput
                                        unit="min"
                                        step="5"
                                        error={!!fieldErrors.drainSpanMin}
                                        errorMessage={fieldErrors.drainSpanMin}
                                        value={form.drainSpanMin}
                                        onChange={(e) => setField('drainSpanMin', e.target.value)}
                                    />
                                </FpFieldRow>

                                <FpFieldRow label="Max burn rate" fieldTip={fieldTips.maxBurnRateLh}>
                                    <FpNumberInput
                                        unit="L/h"
                                        step="5"
                                        error={!!fieldErrors.maxBurnRateLh}
                                        errorMessage={fieldErrors.maxBurnRateLh}
                                        value={form.maxBurnRateLh}
                                        onChange={(e) => setField('maxBurnRateLh', e.target.value)}
                                    />
                                </FpFieldRow>

                            </div>

                            <div className="fp-col">
                                <div className="fp-sec-title fp-sec-title--t0">
                                    <LockIcon sx={{ fontSize: 16 }} />
                                    Calibration
                                </div>

                                    <FpFieldRow fill label="Level Signal Unit" fieldTip={fieldTips.telemetryInput}>
                                            <div className="fp-validated-control">
                                                <FpDropDown
                                                    options={TELEMETRY_INPUT_OPTIONS}
                                                    selectedValue={form.telemetryInput}
                                                    onChange={handleTelemetryInputSelect}
                                                    placeholder="Select level signal unit"
                                                    disabled={isLocked('telemetryInput')}
                                                    error={!!fieldErrors.telemetryInput}
                                                />
                                                {fieldErrors.telemetryInput ? (
                                                    <div className="fp-field-error">{fieldErrors.telemetryInput}</div>
                                                ) : null}
                                            </div>
                                        </FpFieldRow>

                                    {isVoltageTelemetryInput(form.telemetryInput) ? (
                                        <FpFieldRow fill label="Valid voltage range" fieldTip={fieldTips.voltageRange}>
                                            <div className="fp-voltage-range-wrap">
                                                <div className="fp-voltage-range-row">
                                                    <FpNumberInput
                                                        unit="V"
                                                        placeholder="0.00"
                                                        error={!!fieldErrors.voltageMin}
                                                        value={form.voltageMin}
                                                        disable={isLocked('voltageMin')}
                                                        onChange={(e) => setField('voltageMin', e.target.value)}
                                                    />
                                                    <span className="fp-voltage-range-sep">to</span>
                                                    <FpNumberInput
                                                        unit="V"
                                                        placeholder="0.00"
                                                        error={!!fieldErrors.voltageMax}
                                                        value={form.voltageMax}
                                                        disable={isLocked('voltageMax')}
                                                        onChange={(e) => setField('voltageMax', e.target.value)}
                                                    />
                                                </div>
                                                {fieldErrors.voltageMin || fieldErrors.voltageMax ? (
                                                    <div className="fp-field-error fp-field-error--below">
                                                        {fieldErrors.voltageMin || fieldErrors.voltageMax}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </FpFieldRow>
                                    ) : null}

                                    {!isVoltageMode ? (
                                        <FpFieldRow fill label="Level Mapping" fieldTip={fieldTips.levelSource}>
                                            <div className="fp-validated-control">
                                                <FpDropDown
                                                    options={levelMappingOptions}
                                                    selectedValue={levelSourceValue}
                                                    onChange={handleLevelSourceSelect}
                                                    placeholder="Select level mapping"
                                                    disabled={
                                                        form.telemetryInput === 'litrePercentage'
                                                        || (
                                                            isLocked('tableMapEnabled')
                                                            && isLocked('equationMapEnabled')
                                                            && isLocked('tankCapacityDetectionEnabled')
                                                        )
                                                    }
                                                    error={!!fieldErrors.fuelMap}
                                                />
                                                {fieldErrors.fuelMap ? <div className="fp-field-error">{fieldErrors.fuelMap}</div> : null}
                                            </div>
                                        </FpFieldRow>
                                    ) : null}

                                    {!isVoltageMode
                                        && !form.tableMapEnabled
                                        && !form.equationMapEnabled
                                        && !form.tankCapacityDetectionEnabled ? (
                                        <div className="fp-calibration-hint">Select a level mapping to configure calibration.</div>
                                    ) : null}

                                    {form.tableMapEnabled && !isVoltageMode ? (
                                        <div className="fp-map-shell">
                                            <FuelMapTable
                                                fuelMapRows={form.fuelMapRows}
                                                setFuelMapRows={(u) => {
                                                    if (isLocked('fuelMapRows')) return;
                                                    setForm((prev) => ({ ...prev, fuelMapRows: typeof u === 'function' ? u(prev.fuelMapRows) : u }));
                                                }}
                                                disabled={!form.tableMapEnabled || isLocked('fuelMapRows')}
                                                telemetryInput={form.telemetryInput}
                                            />
                                        </div>
                                    ) : null}

                                    { (isVoltageMode || form.equationMapEnabled) ? (
                                        <div className="fp-tier0-block">
                                            <div className="fp-tier0-block-title">
                                                Fuel Map Equation
                                                {form.telemetryInput !== 'litre' ? (
                                                    <>
                                                        {' '}
                                                        <span className="fp-tier0-block-formula">
                                                            {fuelMapEquationFormulaForFormTelemetry(form.telemetryInput)}
                                                        </span>
                                                    </>
                                                ) : null}
                                            </div>
                                            {form.telemetryInput === 'litre' ? (
                                                <div className="fp-equation-inverse-formulas">
                                                    <div className="fp-equation-inverse-title">Inverse formula (ℓ → %)</div>
                                                    <div className="fp-equation-inverse-line">
                                                        Linear (a₂ = 0): {FUEL_MAP_EQUATION_INVERSE_FORMULAS_FOR_LITRE.linear}
                                                    </div>
                                                    <div className="fp-equation-inverse-line">
                                                        Polynomial: {FUEL_MAP_EQUATION_INVERSE_FORMULAS_FOR_LITRE.polynomial}
                                                    </div>
                                                </div>
                                            ) : null}
                                            <div className="fp-equation-shell">
                                                <FuelEquation
                                                    values={form.equationMap}
                                                    onChange={handleEquationChange}
                                                    disabled={(isVoltageMode ? false : !form.equationMapEnabled) || isLocked('equationMap')}
                                                />
                                            </div>
                                            {isVoltageMode && fieldErrors.fuelMap ? (
                                                <div className="fp-field-error">{fieldErrors.fuelMap}</div>
                                            ) : null}
                                        </div>
                                    ) : null}

                                    <div className="fp-tier0-block">
                                        <div className="fp-tier0-block-title">Fuel Level Indicators Mapping</div>
                                        <div className="fp-percentage-mapping-desc">
                                            The percentages will be mapped according to the given table.
                                        </div>
                                        <div className="fp-percentage-shell">
                                            <BatteryPercentageStatusTable
                                                percentageMaps={form.percentageMapping}
                                                setPercentageMaps={(u) => {
                                                    if (isLocked('percentageMapping')) return;
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        percentageMapping: typeof u === 'function'
                                                            ? u(prev.percentageMapping)
                                                            : u,
                                                    }));
                                                }}
                                            />
                                        </div>
                                    </div>
                            </div>

                            <div className="fp-tier0-block fp-gnss-band">
                                <div className="fp-tier0-block-title">GNSS Virtual Fuel Coefficients</div>
                                <div className="fp-gnss-grid">
                                    {[
                                        ['idleLhr', 'Idling Burn Rate L/hr', ''],
                                        ['cityL100km', 'City Consumption Rate L/100km', ''],
                                        ['avgL100km', 'Average Consumption Rate L/100km', ''],
                                        ['hwyL100km', 'Highway Consumption Rate L/100km', ''],
                                        ['penaltyPct', 'Overspeeding Penalty %', ''],
                                        ['penaltyIntervalKmh', 'Penalty Interval km/h', ''],
                                    ].map(([key, gnssLabel, gnssUnit]) => (
                                        <div key={key} className="fp-gnss-mini-row">
                                            <span className="fp-gnss-mini-label">
                                                {gnssLabel}
                                                {fieldTips[key] ? <FpFieldInfoTrigger fieldTip={fieldTips[key]} /> : null}
                                            </span>
                                            <FpNumberInput
                                                tier0
                                                narrow
                                                unit={gnssUnit}
                                                error={!!fieldErrors[key]}
                                                value={form[key]}
                                                disable={isLocked(key)}
                                                onChange={(e) => setField(key, e.target.value)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="fp-desc-block">
                            <FpFieldRow label="Description / Installer notes" stack>
                                <TextArea
                                    descriptionValue={form.description}
                                    setDescriptionValue={(v) => setField('description', v)}
                                    width="100%"
                                />
                            </FpFieldRow>
                        </div>

                        <div className="fp-footer-bar">
                            <span>{isDirty ? 'You have unsaved changes.' : 'All changes saved.'}</span>
                        </div>
                    </div>

                    <div className="add-device-footer">
                        <div className="add-device-button-container">
                            <SecondaryButton label="Cancel" onClickFunction={handleBack} />
                            {isEditMode ? (
                                <OutlinedDeleteButton label="Delete Policy" onClickFunction={() => setDeleteModal(true)} />
                            ) : null}
                            <PrimaryButton label={isEditMode ? 'Update' : 'Save'} onClickFunction={handleSave} disable={!form.name?.trim()} />
                        </div>
                    </div>
                </div>
            </div>

            {deleteModal && (
                <div className="modal-container-table-bg">
                    <Modal setSwitchModalShow={setDeleteModal} switchModalShow={deleteModal} btnConfirm={handleDelete} module="DeleteModal" source="FuelPolicy" dataRow={{ name: form.name }} />
                </div>
            )}

            {activeConfirmOpen && (
                <div className="modal-container-table-bg">
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 440 }}>
                            <Title title="Update active policy?" deleteSubtitle titleSize="1.125rem" subtitleSize="0.875rem" gap="0.5rem" lineHeight="1.5rem">
                                This field is overridden on
                                {' '}
                                <b>{assetsWithOverridesCount}</b>
                                {' '}
                                assets. They keep their custom value. The other
                                {' '}
                                <b>{Math.max(0, assetsAttached - assetsWithOverridesCount)}</b>
                                {' '}
                                assets receive this update on their next packet.
                            </Title>
                            <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'flex-end' }}>
                                <SecondaryButton label="Cancel" onClickFunction={() => setActiveConfirmOpen(false)} />
                                <PrimaryButton label="Proceed" onClickFunction={handleActiveConfirmProceed} />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function AddFuelConfig() {
    return (
        <ErrorBoundary FallbackComponent={formFallback}>
            <FpFieldTooltipProvider>
                <AddFuelConfigInner />
            </FpFieldTooltipProvider>
        </ErrorBoundary>
    );
}
